import { F0DatePicker, F0Text } from '@factorialco/f0-react'
import {
  Input,
  NumberInput,
  Select,
  Stack,
  Switch,
  Textarea,
} from '@factorialco/f0-react/dist/experimental'
import { DynamicFormFieldFragment } from 'generated/resources/graphql'
import { camelCase } from 'lodash'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { DynamicFieldProps } from 'modules/dynamic_forms/types'

/**
 * Returns the string value for a given camelCased field name from the values map.
 */
function getStringValue(
  fieldName: string,
  values: Record<string, string | boolean | undefined>
): string {
  const val = values[fieldName]
  if (typeof val === 'string') return val
  return ''
}

/**
 * Filters allowed values by legal entity and country context when applicable.
 */
function filterAllowedValues(
  allowedValues: DynamicFormFieldFragment['allowedValues'],
  fieldName: string,
  legalEntityId?: string,
  country?: string
) {
  let filtered = allowedValues ?? []

  if (legalEntityId) {
    filtered = filtered.filter(
      (option) => !option.legalEntityIds || option.legalEntityIds.includes(legalEntityId)
    )
  }

  if (country && fieldName !== 'legal_entity_id') {
    filtered = filtered.filter((option) => !option.countries || option.countries.includes(country))
  }

  return filtered
}

/**
 * Converts a date string to a F0DatePicker value.
 */
function stringToDatePickerValue(dateString: string | undefined) {
  if (!dateString) return undefined

  const parsedDate = stringToDate(dateString, 'backend')

  if (!parsedDate.isValid()) return undefined

  const normalizedDate = parsedDate.toDate()

  return {
    granularity: 'day' as const,
    value: {
      from: normalizedDate,
      to: normalizedDate,
    },
  }
}

/**
 * Generic dynamic field renderer.
 *
 * Maps `DynamicFormFieldFragment.fieldType` to the appropriate F0 UI component.
 * Supported V1 types: string, email, enum, boolean, date, integer (with/without allowed values).
 */
export function DynamicField({
  field,
  values,
  onChange,
  onToggle,
  errors,
  context,
}: DynamicFieldProps) {
  const fieldName = camelCase(field.fieldName)
  const error = errors?.[fieldName]

  switch (field.fieldType) {
    case 'string':
    case 'swift':
      return (
        <Input
          key={field.fieldName}
          type="text"
          size="md"
          label={field.label}
          value={getStringValue(fieldName, values)}
          onChange={(value) => onChange(fieldName, value ?? '')}
          error={error}
          required={field.required}
        />
      )

    case 'textarea':
      return (
        <Textarea
          key={field.fieldName}
          label={field.label}
          value={getStringValue(fieldName, values)}
          onChange={(value) => onChange(fieldName, value ?? '')}
          error={error}
        />
      )

    case 'email':
      return (
        <Input
          key={field.fieldName}
          type="email"
          size="md"
          label={field.label}
          value={getStringValue(fieldName, values)}
          onChange={(value) => onChange(fieldName, value ?? '')}
          error={error}
          required={field.required}
        />
      )

    case 'phone':
      return (
        <Input
          key={field.fieldName}
          type="tel"
          size="md"
          label={field.label}
          value={getStringValue(fieldName, values)}
          onChange={(value) => onChange(fieldName, value ?? '')}
          error={error}
          required={field.required}
        />
      )

    case 'enum':
    case 'single_select':
    case 'multi_select': {
      const allowedValues = filterAllowedValues(
        field.allowedValues,
        field.fieldName,
        context?.legalEntityId,
        context?.country
      )

      if (!allowedValues.length) return null

      const options = allowedValues.map((av) => ({
        label: av.value,
        value: av.key,
      }))

      return (
        <Select
          key={field.fieldName}
          label={field.label}
          value={getStringValue(fieldName, values) || undefined}
          onChange={(value: string) => onChange(fieldName, value ?? '')}
          options={options}
          error={error}
          required={field.required}
        />
      )
    }

    case 'boolean':
      return (
        <Stack
          key={field.fieldName}
          inline
          alignItems="center"
          justifyContent="space-between"
          gap="2"
        >
          <F0Text variant="body" content={field.label} />
          <Switch
            hideLabel
            checked={!!values[fieldName]}
            onCheckedChange={(value: boolean) => onToggle?.(fieldName, value)}
          />
        </Stack>
      )

    case 'date':
      return (
        <F0DatePicker
          key={field.fieldName}
          label={field.label}
          value={stringToDatePickerValue(getStringValue(fieldName, values))}
          onChange={(value) => {
            if (value?.value?.from) {
              const dateStr = i18nDateToString(value.value.from, 'backend')
              onChange(fieldName, dateStr ?? '')
            } else {
              onChange(fieldName, '')
            }
          }}
          placeholder={i18n.t('common.select_date')}
        />
      )

    case 'integer':
    case 'numeric': {
      if (field.allowedValues?.length) {
        const allowedValues = filterAllowedValues(
          field.allowedValues,
          field.fieldName,
          context?.legalEntityId,
          context?.country
        )

        if (!allowedValues.length) return null

        const options = allowedValues.map((av) => ({
          label: av.value,
          value: av.key,
        }))

        return (
          <Select
            key={field.fieldName}
            label={field.label}
            value={getStringValue(fieldName, values) || undefined}
            onChange={(value: string) => onChange(fieldName, value ?? '')}
            options={options}
            error={error}
            required={field.required}
          />
        )
      }

      return (
        <NumberInput
          key={field.fieldName}
          size="md"
          locale={i18n.locale}
          label={field.label}
          value={
            getStringValue(fieldName, values) ? Number(getStringValue(fieldName, values)) : null
          }
          onChange={(value) => onChange(fieldName, value !== null ? String(value) : '')}
          error={error}
        />
      )
    }

    default:
      return null
  }
}
